(() => {
class SocialCarousel extends HTMLElement {
  #track;
  #cards = [];
  #previousButton;
  #nextButton;
  #status;
  #progress;
  #shareStatus;
  #feedStatus;
  #snapPoints = [];
  #snapIndex = 0;
  #animationFrame;
  #dragStartX = 0;
  #dragStartScrollLeft = 0;
  #dragged = false;
  #videoObserver;
  #resizeObserver;
  #feedAbortController;
  #lastFeedAttempt = 0;
  #refreshTimer;

  connectedCallback() {
    this.#track = this.querySelector('[data-social-track]');
    if (!this.#track) return;

    this.#previousButton = this.querySelector('[data-social-previous]');
    this.#nextButton = this.querySelector('[data-social-next]');
    this.#status = this.querySelector('[data-social-status]');
    this.#progress = this.querySelector('[data-social-progress]');
    this.#shareStatus = this.querySelector('[data-share-status]');
    this.#feedStatus = this.querySelector('[data-feed-status]');

    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#track.addEventListener('keydown', this.#handleKeydown);
    this.#track.addEventListener('pointerdown', this.#handlePointerDown);
    this.#track.addEventListener('pointermove', this.#handlePointerMove);
    this.#track.addEventListener('pointerup', this.#finishDrag);
    this.#track.addEventListener('pointercancel', this.#finishDrag);
    this.#track.addEventListener('click', this.#preventDraggedClick, true);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('dblclick', this.#handleDoubleClick);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize, { passive: true });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this.#track);
    }

    this.#refreshCards();
    this.#loadFeed();
    const refreshMinutes = Math.max(15, Number.parseInt(this.dataset.refreshMinutes, 10) || 60);
    this.#refreshTimer = window.setInterval(() => {
      if (!document.hidden) this.#loadFeed();
    }, refreshMinutes * 60 * 1000);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#animationFrame);
    window.clearInterval(this.#refreshTimer);
    this.#feedAbortController?.abort();
    this.#videoObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.#track?.removeEventListener('keydown', this.#handleKeydown);
    this.#track?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#track?.removeEventListener('pointermove', this.#handlePointerMove);
    this.#track?.removeEventListener('pointerup', this.#finishDrag);
    this.#track?.removeEventListener('pointercancel', this.#finishDrag);
    this.#track?.removeEventListener('click', this.#preventDraggedClick, true);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('dblclick', this.#handleDoubleClick);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);
    this.#unbindVideos();
  }

  #handleScroll = () => this.#scheduleUpdate();

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = requestAnimationFrame(this.#updateCarousel);
  };

  #handleResize = () => {
    this.#measureSnapPoints();
    this.#scheduleUpdate();
  };

  #refreshCards({ resetScroll = false } = {}) {
    this.#cards = [...this.#track.querySelectorAll('[data-social-card]')];
    this.#cards.forEach((card, index) => {
      card.setAttribute('aria-label', `Post ${index + 1} of ${this.#cards.length}`);
    });

    if (resetScroll) this.#track.scrollTo({ left: 0, behavior: 'auto' });
    this.#snapIndex = 0;
    this.#measureSnapPoints();
    this.#bindVideos();
    this.#observeVideos();
    this.#updateCarousel();
  }

  #measureSnapPoints() {
    if (!this.#track || !this.#cards.length) {
      this.#snapPoints = [];
      return;
    }

    const styles = getComputedStyle(this.#track);
    const padding = Number.parseFloat(styles.paddingLeft) || 0;
    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const currentScroll = this.#track.scrollLeft;
    const trackLeft = this.#track.getBoundingClientRect().left;
    const points = [];

    this.#cards.forEach((card, cardIndex) => {
      const cardLeft = currentScroll + card.getBoundingClientRect().left - trackLeft - padding;
      const left = Math.min(maxScroll, Math.max(0, cardLeft));
      const previous = points[points.length - 1];

      if (previous && Math.abs(previous.left - left) <= 1) {
        if (maxScroll > 1 && left >= maxScroll - 1) previous.cardIndex = cardIndex;
        return;
      }

      points.push({ cardIndex, left });
    });

    this.#snapPoints = points.length ? points : [{ cardIndex: 0, left: 0 }];
  }

  #nearestSnapIndex() {
    if (!this.#snapPoints.length) return 0;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    this.#snapPoints.forEach((point, index) => {
      const distance = Math.abs(this.#track.scrollLeft - point.left);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  }

  #updateCarousel = () => {
    if (!this.#track) return;
    if (!this.#cards.length) {
      if (this.#previousButton) this.#previousButton.disabled = true;
      if (this.#nextButton) this.#nextButton.disabled = true;
      if (this.#status) this.#status.textContent = '0 / 0';
      if (this.#progress) this.#progress.style.transform = 'scaleX(0)';
      return;
    }

    if (!this.#snapPoints.length) this.#measureSnapPoints();
    this.#snapIndex = this.#nearestSnapIndex();
    const currentCardIndex = this.#snapPoints[this.#snapIndex]?.cardIndex ?? 0;
    this.#cards.forEach((card, index) => card.toggleAttribute('aria-current', index === currentCardIndex));

    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const atStart = maxScroll <= 1 || this.#track.scrollLeft <= 2;
    const atEnd = maxScroll <= 1 || this.#track.scrollLeft >= maxScroll - 2;

    if (this.#previousButton) this.#previousButton.disabled = atStart;
    if (this.#nextButton) this.#nextButton.disabled = atEnd;
    if (this.#status) this.#status.textContent = `${currentCardIndex + 1} / ${this.#cards.length}`;
    if (this.#progress) {
      this.#progress.style.transform = `scaleX(${(this.#snapIndex + 1) / this.#snapPoints.length})`;
    }
  };

  #scrollToSnap(index) {
    if (!this.#snapPoints.length) return;
    const nextIndex = Math.max(0, Math.min(index, this.#snapPoints.length - 1));
    this.#snapIndex = nextIndex;
    this.#track.scrollTo({
      left: this.#snapPoints[nextIndex].left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #scrollToCard(index) {
    const cardIndex = Math.max(0, Math.min(index, this.#cards.length - 1));
    let targetIndex = this.#snapPoints.findIndex((point) => point.cardIndex >= cardIndex);
    if (targetIndex < 0) targetIndex = this.#snapPoints.length - 1;
    this.#scrollToSnap(targetIndex);
  }

  #handleKeydown = (event) => {
    let nextSnapIndex;

    this.#snapIndex = this.#nearestSnapIndex();
    if (event.key === 'ArrowLeft') nextSnapIndex = this.#snapIndex - 1;
    if (event.key === 'ArrowRight') nextSnapIndex = this.#snapIndex + 1;
    if (event.key === 'Home') nextSnapIndex = 0;
    if (event.key === 'End') nextSnapIndex = this.#snapPoints.length - 1;
    if (nextSnapIndex === undefined) return;

    event.preventDefault();
    this.#scrollToSnap(nextSnapIndex);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('a, button, input, select, textarea')) return;

    this.#dragStartX = event.clientX;
    this.#dragStartScrollLeft = this.#track.scrollLeft;
    this.#dragged = false;
    this.#track.classList.add('is-dragging');
    this.#track.setPointerCapture(event.pointerId);
  };

  #handlePointerMove = (event) => {
    if (!this.#track.classList.contains('is-dragging')) return;

    const distance = event.clientX - this.#dragStartX;
    if (Math.abs(distance) > 4) this.#dragged = true;
    this.#track.scrollLeft = this.#dragStartScrollLeft - distance;
  };

  #finishDrag = (event) => {
    if (!this.#track.classList.contains('is-dragging')) return;

    this.#track.classList.remove('is-dragging');
    if (this.#track.hasPointerCapture(event.pointerId)) this.#track.releasePointerCapture(event.pointerId);
    this.#updateCarousel();
    this.#scrollToSnap(this.#nearestSnapIndex());
  };

  #preventDraggedClick = (event) => {
    if (!this.#dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };

  #handleClick = (event) => {
    if (event.target.closest('[data-social-previous]')) {
      this.#snapIndex = this.#nearestSnapIndex();
      this.#scrollToSnap(this.#snapIndex - 1);
      return;
    }

    if (event.target.closest('[data-social-next]')) {
      this.#snapIndex = this.#nearestSnapIndex();
      this.#scrollToSnap(this.#snapIndex + 1);
      return;
    }

    const likeButton = event.target.closest('[data-like-toggle]');
    if (likeButton) {
      this.#togglePressed(likeButton, 'Like post', 'Unlike post');
      return;
    }

    const saveButton = event.target.closest('[data-save-toggle]');
    if (saveButton) {
      this.#togglePressed(saveButton, 'Save post', 'Remove saved post');
      return;
    }

    const shareButton = event.target.closest('[data-share-post]');
    if (shareButton) {
      this.#sharePost(shareButton);
      return;
    }

    const captionButton = event.target.closest('[data-caption-toggle]');
    if (captionButton) {
      this.#toggleCaption(captionButton);
      return;
    }

    const playButton = event.target.closest('[data-video-toggle]');
    if (playButton) {
      this.#toggleVideo(playButton);
      return;
    }

    const soundButton = event.target.closest('[data-sound-toggle]');
    if (soundButton) this.#toggleSound(soundButton);
  };

  #handleDoubleClick = (event) => {
    const media = event.target.closest('[data-social-media]');
    if (!media || event.target.closest('button, a')) return;

    const card = media.closest('[data-social-card]');
    const likeButton = card?.querySelector('[data-like-toggle]');
    if (likeButton && likeButton.getAttribute('aria-pressed') !== 'true') {
      this.#togglePressed(likeButton, 'Like post', 'Unlike post');
    }

    const burst = card?.querySelector('.social-carousel-card__heart-burst');
    if (!burst || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    burst.classList.remove('is-active');
    requestAnimationFrame(() => burst.classList.add('is-active'));
  };

  #togglePressed(button, inactiveLabel, activeLabel) {
    const isPressed = button.getAttribute('aria-pressed') === 'true';
    button.setAttribute('aria-pressed', (!isPressed).toString());
    button.setAttribute('aria-label', isPressed ? inactiveLabel : activeLabel);
  }

  #toggleCaption(button) {
    const caption = button.closest('.social-carousel-card__caption-wrap')?.querySelector('[data-caption]');
    if (!caption) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', (!isExpanded).toString());
    button.textContent = isExpanded ? 'more' : 'less';
    caption.classList.toggle('is-expanded', !isExpanded);
  }

  async #sharePost(button) {
    const url = button.dataset.shareUrl;
    const title = button.dataset.shareTitle || document.title;
    if (!url) return;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        this.#announce('Post shared.');
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        this.#announce('Post link copied to clipboard.');
      } else {
        this.#announce('Open the post to copy its link.');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') this.#announce('The post link could not be shared.');
    }
  }

  #announce(message) {
    if (!this.#shareStatus) return;
    this.#shareStatus.textContent = '';
    requestAnimationFrame(() => {
      this.#shareStatus.textContent = message;
    });
  }

  #bindVideos() {
    this.#cards.forEach((card) => card.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.addEventListener('play', this.#handleVideoStateChange);
      video.addEventListener('pause', this.#handleVideoStateChange);
      video.addEventListener('volumechange', this.#handleVideoStateChange);
      this.#syncVideoControls(video);
    }));
  }

  #unbindVideos() {
    this.#cards.forEach((card) => card.querySelectorAll('video').forEach((video) => {
      video.removeEventListener('play', this.#handleVideoStateChange);
      video.removeEventListener('pause', this.#handleVideoStateChange);
      video.removeEventListener('volumechange', this.#handleVideoStateChange);
    }));
  }

  #hydrateVideo(video) {
    const lazySources = video.querySelectorAll('source[data-src]');
    if (!lazySources.length) return;

    lazySources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
  }

  #toggleVideo(button) {
    const video = button.closest('[data-social-card]')?.querySelector('video');
    if (!video) return;

    this.#hydrateVideo(video);
    if (video.paused) {
      video.play().catch(() => this.#syncVideoControls(video));
    } else {
      video.pause();
    }
  }

  #toggleSound(button) {
    const video = button.closest('[data-social-card]')?.querySelector('video');
    if (!video) return;

    this.#hydrateVideo(video);
    const shouldUnmute = video.muted;
    if (shouldUnmute) {
      this.querySelectorAll('video').forEach((otherVideo) => {
        if (otherVideo === video) return;
        otherVideo.muted = true;
      });
    }

    video.muted = !shouldUnmute;
    video.play().catch(() => this.#syncVideoControls(video));
    this.#syncVideoControls(video);
  }

  #handleVideoStateChange = (event) => this.#syncVideoControls(event.currentTarget);

  #syncVideoControls(video) {
    const card = video.closest('[data-social-card]');
    const playButton = card?.querySelector('[data-video-toggle]');
    const soundButton = card?.querySelector('[data-sound-toggle]');

    if (playButton) {
      const isPlaying = !video.paused;
      playButton.setAttribute('aria-pressed', isPlaying.toString());
      playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }

    if (soundButton) {
      const isSoundOn = !video.muted;
      soundButton.setAttribute('aria-pressed', isSoundOn.toString());
      soundButton.setAttribute('aria-label', isSoundOn ? 'Turn sound off' : 'Turn sound on');
    }
  }

  #observeVideos() {
    this.#videoObserver?.disconnect();
    const videos = this.#cards.flatMap((card) => [...card.querySelectorAll('video')]);
    if (!videos.length || !('IntersectionObserver' in window)) return;

    const shouldAutoplay = this.dataset.autoplayVideos === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            this.#hydrateVideo(video);
            if (shouldAutoplay) video.play().catch(() => this.#syncVideoControls(video));
            return;
          }

          video.pause();
          video.muted = true;
          this.#syncVideoControls(video);
        });
      },
      { root: this.#track, rootMargin: '0px 35%', threshold: [0, 0.55] }
    );

    videos.forEach((video) => this.#videoObserver.observe(video));
  }

  async #loadFeed() {
    const feedUrl = this.dataset.feedUrl;
    if (!feedUrl || this.dataset.feedState === 'loading') {
      if (!feedUrl) this.dataset.feedState = 'fallback';
      return;
    }

    let endpoint;
    try {
      endpoint = new URL(feedUrl);
      if (endpoint.protocol !== 'https:' || endpoint.hostname !== 'instafeed.nfcube.com') {
        throw new Error('Unexpected feed endpoint.');
      }
    } catch (_error) {
      this.dataset.feedState = 'fallback';
      return;
    }

    this.#lastFeedAttempt = Date.now();
    this.dataset.feedState = 'loading';
    this.#track.setAttribute('aria-busy', 'true');
    this.#feedAbortController?.abort();
    this.#feedAbortController = new AbortController();

    try {
      const response = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
        signal: this.#feedAbortController.signal,
      });
      if (!response.ok) throw new Error('Instagram feed request failed.');

      const payload = await response.json();
      if (Number.parseInt(payload?.meta?.code, 10) !== 200 || !Array.isArray(payload?.data)) {
        throw new Error('Instagram feed is not connected.');
      }

      const account = this.#normaliseAccount(this.dataset.instagramAccount || 'honeyforlife__');
      const limit = Math.max(1, Number.parseInt(this.dataset.postLimit, 10) || 8);
      const posts = payload.data
        .map((post) => this.#normalisePost(post, payload.meta, account))
        .filter(Boolean)
        .sort((first, second) => second.timestamp - first.timestamp)
        .slice(0, limit);

      if (!posts.length) throw new Error('No matching Honey For Life posts were returned.');
      if (!this.isConnected) return;

      this.#renderFeed(posts);
      this.dataset.feedState = 'live';
      if (this.#feedStatus) {
        this.#feedStatus.textContent = 'Latest posts from @honeyforlife__ loaded.';
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (!this.isConnected) return;
      this.dataset.feedState = this.#cards.some((card) => card.hasAttribute('data-feed-post')) ? 'live' : 'fallback';
      if (!this.#cards.length && this.#feedStatus) {
        this.#feedStatus.textContent = 'Instagram posts are temporarily unavailable.';
      }
    } finally {
      if (this.isConnected) this.#track.removeAttribute('aria-busy');
    }
  }

  #normalisePost(post, meta, account) {
    if (!post || typeof post !== 'object') return null;

    const postAccount = this.#normaliseAccount(post.user?.username || meta?.username || '');
    if (postAccount !== account) return null;

    const carousel = Array.isArray(post.carousel) ? post.carousel : [];
    const firstCarouselItem = carousel[0] || {};
    const rawType = String(post.type || post.media_type || '').toLowerCase();
    let type = rawType.includes('video') || rawType.includes('reel') ? 'video' : 'image';
    const imageUrl = this.#safeMediaUrl(
      post.images?.standard_resolution?.url ||
        post.thumbnail_url ||
        post.image_url ||
        (type === 'image' ? post.media_url : '') ||
        firstCarouselItem.thumbnail_url ||
        firstCarouselItem.media_url
    );
    const videoUrl = this.#safeMediaUrl(
      post.videos?.standard_resolution?.url ||
        post.video_url ||
        (type === 'video' ? post.media_url : '')
    );
    if (type === 'video' && !videoUrl && imageUrl) type = 'image';
    if ((type === 'video' && !videoUrl) || (type === 'image' && !imageUrl)) return null;

    const link = this.#safeInstagramUrl(post.link || post.permalink);
    if (!link) return null;

    const caption = typeof post.caption === 'string' ? post.caption : post.caption?.text;
    const timestamp = this.#timestamp(post.created_time || post.timestamp);
    const mediaCount = Math.max(1, carousel.length || Number.parseInt(post.media_count, 10) || 1);
    const format = mediaCount > 1 ? 'carousel' : type === 'video' ? 'reel' : 'post';

    return {
      id: String(post.id || link),
      type,
      format,
      mediaCount,
      imageUrl,
      videoUrl,
      link,
      caption: String(caption || '').trim(),
      hashtags: this.#extractHashtags(post.tags, caption),
      alt: String(post.alt_text || caption || 'Honey For Life Instagram post').trim(),
      likes: Math.max(0, Number.parseInt(post.likes?.count ?? post.likes_count, 10) || 0),
      comments: Math.max(0, Number.parseInt(post.comments?.count ?? post.comments_count, 10) || 0),
      timestamp,
      profilePicture: this.#safeMediaUrl(meta?.profile_picture),
    };
  }

  #renderFeed(posts) {
    this.#unbindVideos();
    this.#videoObserver?.disconnect();

    const fragment = document.createDocumentFragment();
    posts.forEach((post, index) => fragment.append(this.#createFeedCard(post, index, posts.length)));
    this.#track.replaceChildren(fragment);
    this.#refreshCards({ resetScroll: true });
  }

  #createFeedCard(post, index, total) {
    const card = document.createElement('article');
    card.className = 'social-carousel-card';
    card.setAttribute('data-social-card', '');
    card.setAttribute('data-feed-post', '');
    card.setAttribute('role', 'listitem');
    card.setAttribute('aria-roledescription', 'slide');
    card.setAttribute('aria-label', 'Post ' + (index + 1) + ' of ' + total);

    const template = document.createElement('template');
    template.innerHTML =
      '<div class="social-carousel-card__topbar">' +
        '<div class="social-carousel-card__account">' +
          '<span data-profile-picture></span>' +
          '<span class="social-carousel-card__account-copy">' +
            '<span class="social-carousel-card__username">honeyforlife__</span>' +
            '<span class="social-carousel-card__location">Western Australia</span>' +
          '</span>' +
        '</div>' +
        '<a class="social-carousel-card__more-link" data-post-link target="_blank" rel="noopener noreferrer"><span aria-hidden="true">•••</span></a>' +
      '</div>' +
      '<div class="social-carousel-card__media" style="--social-carousel-media-fit: cover; --social-carousel-media-position: center center;" data-social-media>' +
        '<span data-media-slot></span>' +
        '<span class="social-carousel-card__format" data-format-badge hidden>' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="1.6"></rect><path d="m7 3 4 5m2-5 4 5M3.5 8h17" fill="none" stroke="currentColor" stroke-width="1.6"></path><path d="m10 12.2 5 2.8-5 2.8v-5.6Z" fill="currentColor"></path></svg>' +
          '<span data-format-label></span>' +
        '</span>' +
        '<div class="social-carousel-card__video-controls" data-video-controls hidden>' +
          '<button type="button" class="social-carousel-card__media-control" data-video-toggle aria-label="Play video" aria-pressed="false">' +
            '<svg class="social-carousel-card__play-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 9 6-9 6V6Z" fill="currentColor"></path></svg>' +
            '<svg class="social-carousel-card__pause-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z" fill="currentColor"></path></svg>' +
          '</button>' +
        '</div>' +
        '<span class="social-carousel-card__heart-burst" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 20.5 4.2 13A5.3 5.3 0 0 1 12 5.8 5.3 5.3 0 0 1 19.8 13L12 20.5Z" fill="currentColor"></path></svg></span>' +
      '</div>' +
      '<div class="social-carousel-card__body">' +
        '<div class="social-carousel-card__actions">' +
          '<div class="social-carousel-card__actions-primary">' +
            '<button class="social-carousel-card__action" type="button" data-like-toggle aria-label="Like post" aria-pressed="false">' +
              '<svg class="social-carousel-card__heart-outline" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path></svg>' +
              '<svg class="social-carousel-card__heart-filled" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" fill="currentColor"></path></svg>' +
            '</button>' +
            '<a class="social-carousel-card__action" data-post-link target="_blank" rel="noopener noreferrer" aria-label="View comments on Instagram"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.8 9.8 0 0 1-4.2-1L3 20l1.2-4.5A8.2 8.2 0 0 1 3 11.3a8.5 8.5 0 0 1 9-8.2 8.5 8.5 0 0 1 9 8.4Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path></svg></a>' +
            '<button class="social-carousel-card__action" type="button" data-share-post aria-label="Share post"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22 2 11 13" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path><path d="m22 2-7 20-4-9-9-4 20-7Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>' +
          '</div>' +
          '<button class="social-carousel-card__action" type="button" data-save-toggle aria-label="Save post" aria-pressed="false">' +
            '<svg class="social-carousel-card__save-outline" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14v18l-7-4-7 4V3Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path></svg>' +
            '<svg class="social-carousel-card__save-filled" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14v18l-7-4-7 4V3Z" fill="currentColor"></path></svg>' +
          '</button>' +
        '</div>' +
        '<p class="social-carousel-card__engagement" data-engagement></p>' +
        '<div class="social-carousel-card__caption-wrap" data-caption-wrap>' +
          '<p class="social-carousel-card__caption" data-caption><strong>honeyforlife__</strong> <span class="social-carousel-card__caption-text" data-caption-text></span></p>' +
          '<button class="social-carousel-card__caption-toggle" type="button" data-caption-toggle aria-expanded="false" hidden>more</button>' +
        '</div>' +
        '<p class="social-carousel-card__hashtags" data-hashtags hidden></p>' +
        '<div class="social-carousel-card__comments"><a data-post-link data-comments-link target="_blank" rel="noopener noreferrer"></a></div>' +
        '<div class="social-carousel-card__meta"><time data-published></time><a data-post-link target="_blank" rel="noopener noreferrer">View post <span aria-hidden="true">↗</span></a></div>' +
      '</div>';

    card.append(template.content.cloneNode(true));

    card.querySelectorAll('[data-post-link]').forEach((link) => {
      link.href = post.link;
      if (link.classList.contains('social-carousel-card__more-link')) {
        link.setAttribute('aria-label', 'Open Instagram post by @honeyforlife__');
      }
    });

    const profileSlot = card.querySelector('[data-profile-picture]');
    if (post.profilePicture) {
      const avatar = document.createElement('img');
      avatar.className = 'social-carousel-card__avatar';
      avatar.src = post.profilePicture;
      avatar.alt = '';
      avatar.loading = 'lazy';
      avatar.width = 42;
      avatar.height = 42;
      profileSlot.replaceWith(avatar);
    } else {
      profileSlot.className = 'social-carousel-card__avatar social-carousel-card__avatar--fallback';
      profileSlot.setAttribute('aria-hidden', 'true');
      profileSlot.textContent = 'H';
    }

    const mediaSlot = card.querySelector('[data-media-slot]');
    if (post.type === 'video') {
      const video = document.createElement('video');
      video.className = 'social-carousel-card__asset social-carousel-card__video';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('aria-label', post.alt);
      if (post.imageUrl) video.poster = post.imageUrl;
      const source = document.createElement('source');
      source.dataset.src = post.videoUrl;
      video.append(source);
      mediaSlot.replaceWith(video);
      card.querySelector('[data-video-controls]').hidden = false;
    } else {
      const image = document.createElement('img');
      image.className = 'social-carousel-card__asset';
      image.src = post.imageUrl;
      image.alt = post.alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaSlot.replaceWith(image);
    }

    if (post.format !== 'post') {
      const badge = card.querySelector('[data-format-badge]');
      badge.hidden = false;
      badge.setAttribute('aria-label', post.format === 'carousel' ? 'Carousel with ' + post.mediaCount + ' items' : 'Reel');
      card.querySelector('[data-format-label]').textContent = post.format === 'carousel' ? String(post.mediaCount) : 'Reel';
    }

    const shareButton = card.querySelector('[data-share-post]');
    shareButton.dataset.shareUrl = post.link;
    shareButton.dataset.shareTitle = 'Instagram post by @honeyforlife__';

    card.querySelector('[data-engagement]').textContent =
      post.likes > 0 ? this.#formatNumber(post.likes) + ' likes' : 'Latest from @honeyforlife__';

    const captionWrap = card.querySelector('[data-caption-wrap]');
    if (post.caption) {
      card.querySelector('[data-caption-text]').textContent = post.caption;
      if (post.caption.length > 160) {
        captionWrap.classList.add('has-more');
        card.querySelector('[data-caption-toggle]').hidden = false;
      }
    } else {
      captionWrap.hidden = true;
    }

    if (post.hashtags.length) {
      const hashtags = card.querySelector('[data-hashtags]');
      hashtags.hidden = false;
      hashtags.textContent = post.hashtags.join(' ');
    }

    const commentsLink = card.querySelector('[data-comments-link]');
    commentsLink.textContent =
      post.comments > 0
        ? 'View all ' + this.#formatNumber(post.comments) + ' comments on Instagram'
        : 'View comments on Instagram';

    const published = card.querySelector('[data-published]');
    if (post.timestamp > 0) {
      const date = new Date(post.timestamp);
      published.dateTime = date.toISOString();
      published.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    } else {
      published.textContent = 'Recent';
    }

    return card;
  }

  #normaliseAccount(value) {
    return String(value || '').trim().replace(/^@/, '').toLowerCase();
  }

  #safeMediaUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(String(value), 'https://www.instagram.com');
      return url.protocol === 'https:' ? url.href : '';
    } catch (_error) {
      return '';
    }
  }

  #safeInstagramUrl(value) {
    const url = this.#safeMediaUrl(value);
    if (!url) return '';
    const hostname = new URL(url).hostname;
    return hostname === 'instagram.com' || hostname === 'www.instagram.com' || hostname.endsWith('.instagram.com') ? url : '';
  }

  #timestamp(value) {
    if (!value) return 0;
    if (typeof value === 'number' || /^\d+$/.test(String(value))) {
      const number = Number(value);
      return number < 100000000000 ? number * 1000 : number;
    }
    const parsed = Date.parse(String(value));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  #extractHashtags(tags, caption) {
    const explicit = Array.isArray(tags)
      ? tags
      : typeof tags === 'string'
        ? tags.split(/[\s,]+/)
        : [];
    const fromCaption = String(caption || '').match(/#[^\s#.,!?;:()[\]{}]+/g) || [];
    const unique = new Set();

    [...explicit, ...fromCaption].forEach((tag) => {
      const clean = String(tag || '').trim();
      if (!clean) return;
      unique.add(clean.startsWith('#') ? clean : '#' + clean);
    });
    return [...unique].slice(0, 12);
  }

  #formatNumber(value) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      notation: value >= 10000 ? 'compact' : 'standard',
      maximumFractionDigits: 1,
    }).format(value);
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#cards.forEach((card) => card.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.muted = true;
      }));
      return;
    }

    const refreshMinutes = Math.max(15, Number.parseInt(this.dataset.refreshMinutes, 10) || 60);
    if (Date.now() - this.#lastFeedAttempt >= refreshMinutes * 60 * 1000) this.#loadFeed();
  };

  #handleBlockSelect = (event) => {
    const card = event.target.closest?.('[data-social-card]');
    if (!card || !this.contains(card)) return;
    this.#scrollToCard(this.#cards.indexOf(card));
  };
}

if (!customElements.get('social-carousel')) {
  customElements.define('social-carousel', SocialCarousel);
}
})();
